import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Car, Mail, Lock, User, Eye, EyeOff, Globe } from 'lucide-react';
import axios from 'axios';
import T from '@/components/StyledTranslation';
import { AuthShell } from '@/components/AuthShell';
import SEO from '@/components/SEO';
import { API } from '@/config';

// Placeholder translations for 9 languages
const namePlaceholders = {
  en: 'John Doe', ar: 'أحمد علي', sv: 'Erik Svensson',
  tr: 'Ahmet Yılmaz', fa: 'علی محمدی', so: 'Maxamed Cali',
  pl: 'Jan Kowalski', es: 'Juan García', de: 'Max Müller'
};

const emailPlaceholders = {
  en: 'email@example.com', ar: 'email@example.com', sv: 'email@exempel.se',
  tr: 'email@ornek.com', fa: 'email@example.com', so: 'email@tusaale.com',
  pl: 'email@przyklad.pl', es: 'email@ejemplo.com', de: 'email@beispiel.de'
};

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [language, setLanguage] = useState(i18n.language);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear any lingering toasts when page loads
  useEffect(() => {
    toast.dismiss();
  }, []);

  // Change viewport for mobile on this page only
  React.useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport ? viewport.getAttribute('content') : '';
    
    // Set mobile-friendly viewport
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5');
    }
    
    // Restore original viewport on unmount
    return () => {
      if (viewport && originalContent) {
        viewport.setAttribute('content', originalContent);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[REGISTER] Form submitted', { name, email, language });
    
    if (password !== confirmPassword) {
      console.error('[REGISTER] Password mismatch');
      const mismatchMsg = {
        en: 'Passwords do not match',
        ar: 'كلمات المرور غير متطابقة',
        sv: 'Lösenorden matchar inte'
      };
      toast.error(mismatchMsg[i18n.language] || mismatchMsg.en);
      return;
    }
    
    if (password.length < 6) {
      console.error('[REGISTER] Password too short');
      const shortMsg = {
        en: 'Password must be at least 6 characters',
        ar: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
        sv: 'Lösenordet måste vara minst 6 tecken'
      };
      toast.error(shortMsg[i18n.language] || shortMsg.en);
      return;
    }

    setLoading(true);

    try {
      console.log('[REGISTER] Sending request to:', `${API}/auth/register`);
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        language
      });
      console.log('[REGISTER] Registration successful:', response.data);
      
      // Check if email verification is required
      if (response.data.requires_verification) {
        // Show success message based on language
        const checkEmailMsg = {
          en: 'Registration successful! Please check your email for verification code.',
          ar: 'تم التسجيل بنجاح! يرجى التحقق من بريدك الإلكتروني للحصول على رمز التحقق.',
          sv: 'Registreringen lyckades! Kontrollera din e-post för verifieringskoden.'
        };
        toast.success(checkEmailMsg[i18n.language] || checkEmailMsg.en);
        
        // حفظ بيانات تسجيل الدخول مؤقتاً لتسجيل الدخول التلقائي بعد التحقق
        sessionStorage.setItem('pendingLoginEmail', email);
        sessionStorage.setItem('pendingLoginPassword', password);
        sessionStorage.setItem('pendingLoginLanguage', language);
        
        // Navigate to email verification page
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        // Admin or already verified - login directly
        const authToken = response.data.token;
        localStorage.setItem('token', authToken);
        toast.success(t('auth.registrationSuccess') || 'Registration successful!');
        
        // Google Ads Conversion Tracking - Registration
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-16862618533/signup',
            'event_category': 'registration',
            'event_label': 'user_signup'
          });
        }
        
        // التحقق من وجود خطة معلقة
        const pendingPlanId = localStorage.getItem('pendingPlanId');
        const pendingPlanPrice = localStorage.getItem('pendingPlanPrice');
        
        // إذا كانت خطة مدفوعة - توجيه مباشر لنظام الدفع
        if (pendingPlanId && pendingPlanPrice && parseFloat(pendingPlanPrice) > 0) {
          try {
            const checkoutResponse = await axios.post(
              `${API}/checkout/session?plan_id=${pendingPlanId}&licenseType=${encodeURIComponent(localStorage.getItem('pendingPlanLicense') || 'b_korkort')}`,
              {},
              { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (checkoutResponse.data.url) {
              localStorage.removeItem('pendingPlanId');
              localStorage.removeItem('pendingPlanLicense');
              localStorage.removeItem('pendingPlanPrice');
              window.location.href = checkoutResponse.data.url;
              return;
            }
          } catch (err) {
            console.error('Checkout error:', err);
            // في حالة الفشل، توجيه لصفحة الأسعار
            navigate('/priser');
            return;
          }
        }
        
        // إذا كانت خطة مجانية - توجيه للاختبارات
        if (pendingPlanId && pendingPlanPrice && parseFloat(pendingPlanPrice) <= 0) {
          localStorage.removeItem('pendingPlanId');
          localStorage.removeItem('pendingPlanLicense');
          localStorage.removeItem('pendingPlanPrice');
          navigate('/teoriprov');
          return;
        }
        
        // Check for post-register redirect intent (e.g., from "Subscribe Now" button)
        const postRegisterRedirect = localStorage.getItem('postRegisterRedirect');
        if (postRegisterRedirect) {
          localStorage.removeItem('postRegisterRedirect');
          navigate(postRegisterRedirect);
          return;
        }
        
        // Navigate based on user role
        if (response.data.user?.is_admin) {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('[REGISTER] Registration error:', error);
      console.error('[REGISTER] Error details:', error.response?.data);
      
      // Check if the error is about existing user (support multiple languages)
      const errorDetail = error.response?.data?.detail || '';
      const existingUserPatterns = [
        'already', 'exists', 'registered', 'redan registrerad', 'مسجل', 'kayıtlı', 'zarejestrowany'
      ];
      const isExistingUser = existingUserPatterns.some(pattern => 
        errorDetail.toLowerCase().includes(pattern.toLowerCase())
      );
      
      if (isExistingUser) {
        const alreadyExistsMsg = {
          en: 'This email is already registered. Please login instead.',
          ar: 'هذا البريد الإلكتروني مسجل مسبقاً. يرجى تسجيل الدخول بدلاً من ذلك.',
          sv: 'Denna e-post är redan registrerad. Logga in istället.',
          tr: 'Bu e-posta zaten kayıtlı. Lütfen giriş yapın.',
          fa: 'این ایمیل قبلاً ثبت شده است. لطفاً وارد شوید.',
          so: 'Emaylkaan horay ayaa loo diiwaangeliyay. Fadlan soo gal.',
          pl: 'Ten e-mail jest już zarejestrowany. Zaloguj się.',
          es: 'Este correo ya está registrado. Por favor, inicia sesión.',
          de: 'Diese E-Mail ist bereits registriert. Bitte melden Sie sich an.'
        };
        toast.error(alreadyExistsMsg[i18n.language] || alreadyExistsMsg.en);
      } else {
        toast.error(error.response?.data?.detail || t('common.error') || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell mode="register" testId="register-page">
      <SEO page="register" />
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-1"><T k="auth.register" /></h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4"><T k="auth.hasAccount" /> <Link to="/logga-in" className="font-semibold text-emerald-600 hover:underline"><T k="auth.login" /></Link></p>

            {/* Social Sign Up Buttons - Facebook temporarily hidden pending Meta Business Verification */}
            <div className="grid grid-cols-1 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const redirectUri = window.location.origin + '/auth/callback';
                    const response = await axios.get(`${API}/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}&source=register`);
                    window.location.href = response.data.url;
                  } catch (error) {
                    console.error('Error getting Google URL:', error);
                    toast.error(t('common.error'));
                  }
                }}
                className="h-10 sm:h-11 rounded-xl border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-2"
                data-testid="google-register-btn"
                title={t('auth.googleSignUp')}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{({ sv: 'Fortsätt med Google', ar: 'المتابعة عبر Google', en: 'Continue with Google', tr: 'Google ile devam et', fa: 'ادامه با Google', so: 'Ku sii wad Google', pl: 'Kontynuuj z Google', es: 'Continuar con Google', de: 'Weiter mit Google' })[i18n.language] || 'Continue with Google'}</span>
              </Button>

              {/* Facebook button hidden until Meta Business Verification is approved. Re-enable by setting SHOW_FACEBOOK to true. */}
              {false && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const ua = navigator.userAgent || navigator.vendor || '';
                    const isInApp = /FBAN|FBAV|FB_IAB|FB4A|Instagram|Messenger|TikTok|LinkedInApp|Line\/|MicroMessenger/i.test(ua);
                    if (isInApp) {
                      const msg = i18n.language === 'ar'
                        ? 'يرجى فتح هذا الرابط في متصفح Chrome أو Safari.\n\nانسخ: https://drivemaster.se/registrera والصقه في Chrome'
                        : i18n.language === 'sv'
                          ? 'Öppna länken i Chrome eller Safari.\n\nKopiera: https://drivemaster.se/registrera och klistra in i Chrome'
                          : 'Please open this link in Chrome or Safari.\n\nCopy: https://drivemaster.se/registrera and paste in Chrome';
                      alert(msg);
                      return;
                    }
                    const redirectUri = window.location.origin + '/auth/callback';
                    const response = await axios.get(`${API}/auth/facebook/url?redirect_uri=${encodeURIComponent(redirectUri)}&source=register_fb`);
                    window.location.href = response.data.url;
                  } catch (error) {
                    console.error('Error getting Facebook URL:', error);
                    toast.error(t('common.error'));
                  }
                }}
                className="h-10 sm:h-11 rounded-xl bg-[#1877F2] hover:bg-[#166FE5] text-white border-0 flex items-center justify-center gap-2"
                data-testid="facebook-register-btn"
                title={i18n.language === 'ar' ? 'التسجيل عبر Facebook' : 'Sign up with Facebook'}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm font-semibold">Facebook</span>
              </Button>
              )}
            </div>
<div className="my-4">
            {/* Divider */}
            <div className="relative my-2 sm:my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-4 bg-white dark:bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
                  {t('auth.or')}
                </span>
              </div>
            </div>

</div>
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-sm text-slate-700"><T k="auth.name" /></Label>
              <div className="relative">
                <User className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="ps-9 sm:ps-10 h-10 sm:h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-emerald-500 focus-visible:bg-white transition-colors text-sm"
                  placeholder={namePlaceholders[lang] || namePlaceholders.en}
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm text-slate-700"><T k="auth.email" /></Label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ps-9 sm:ps-10 h-10 sm:h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-emerald-500 focus-visible:bg-white transition-colors text-sm"
                  placeholder={emailPlaceholders[lang] || emailPlaceholders.en}
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            {/* Password fields in a row on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="password" className="text-sm text-slate-700"><T k="auth.password" /></Label>
                <div className="relative">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="ps-9 sm:ps-10 pe-9 h-10 sm:h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-emerald-500 focus-visible:bg-white transition-colors text-sm"
                    placeholder="••••••••"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirmPassword" className="text-sm text-slate-700"><T k="auth.confirmPassword" /></Label>
                <div className="relative">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="ps-9 sm:ps-10 h-10 sm:h-11 rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus-visible:ring-emerald-500 focus-visible:bg-white transition-colors text-sm"
                    placeholder="••••••••"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold shadow-[0_10px_24px_-10px_rgba(16,185,129,0.9)] active:scale-[0.99] transition-transform text-sm sm:text-base"
              data-testid="register-submit"
            >
              {loading ? t('common.loading') : t('auth.registerBtn')}
            </Button>


            <p className="text-xs text-slate-400 text-center mt-2">
              {t('auth.freeTrialQuestions')}
            </p>
          </form>
    </AuthShell>
  );
};

export default RegisterPage;
